import type {
  DataverseBot,
  DataverseTool,
} from "@p0security/shared/integrations/resources/copilot-studio/types";
import {
  type AIAgent,
  type AIConnector,
  type McpClientAgentData,
  getAgentId,
  isAIPlatform,
} from "@p0security/shared/types/ai-inventory";
import {
  type ItemAssessmentScope,
  toKey,
} from "@p0security/shared/types/assessment";
import { useAuthFetch } from "components/Login/hook";
import { useGuardedEffect } from "hooks/useGuardedEffect";
import { uniqBy } from "lodash-es";
import { useCallback, useState } from "react";
import { Cancellation } from "utils/cancel";

import {
  toCopilotStudioAgent,
  toCopilotStudioConnector,
} from "../platforms/copilotStudio.ts";
import { toLocalAgent } from "../platforms/localAgents.ts";

export type InventoryData = {
  agents: AIAgent[];
  connectors: AIConnector[];
  loading: boolean;
  error?: Error;
};

export type InventoryArgs = {
  assessmentId: string;
  jobId: string;
  scopes: ItemAssessmentScope[];
};

const fetchJson = async (
  authFetch: ReturnType<typeof useAuthFetch>,
  url: string,
  cancellation: Cancellation
): Promise<unknown[]> => {
  const res = await authFetch(url, { method: "GET", cancellation });
  if (!res || cancellation.isCancelled) return [];
  const raw = (await res.json()) as unknown;
  return Array.isArray(raw) ? raw : [];
};

export const useInventoryData = (args: InventoryArgs): InventoryData => {
  const authFetch = useAuthFetch();
  const [agents, setAgents] = useState<AIAgent[]>([]);
  const [connectors, setConnectors] = useState<AIConnector[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  // Serialize scopes to a stable dep so a new array reference on each render
  // does not re-trigger the effect when the contents have not changed.
  const scopesKey = JSON.stringify(args.scopes);

  useGuardedEffect(
    (cancellation) => async () => {
      setLoading(true);
      setError(undefined);
      try {
        // Only AI scopes have /agents and /tools endpoints.
        const aiScopes = args.scopes.filter((scope) =>
          isAIPlatform(scope.integration)
        );
        const perScope = await Promise.all(
          aiScopes.map(async (scope) => {
            const base =
              `assessment/${encodeURIComponent(args.assessmentId)}` +
              `/job/${encodeURIComponent(args.jobId)}` +
              `/scope/${encodeURIComponent(toKey(scope))}`;
            const agentsRawPromise = fetchJson(
              authFetch,
              `${base}/agents`,
              cancellation
            );
            const toolsRawPromise =
              scope.integration === "copilot-studio"
                ? fetchJson(authFetch, `${base}/tools`, cancellation)
                : Promise.resolve([] as unknown[]);
            const [agentsRaw, toolsRaw] = await Promise.all([
              agentsRawPromise,
              toolsRawPromise,
            ]);
            if (cancellation.isCancelled) {
              return {
                agents: [] as AIAgent[],
                connectors: [] as AIConnector[],
              };
            }
            switch (scope.integration) {
              case "copilot-studio":
                return {
                  agents: (agentsRaw as DataverseBot[]).map((b) =>
                    toCopilotStudioAgent(b, {
                      id: scope.id,
                      label: scope.label ?? scope.id,
                    })
                  ),
                  connectors: (toolsRaw as DataverseTool[]).map(
                    toCopilotStudioConnector
                  ),
                };
              case "local-agents":
                return {
                  agents: (agentsRaw as McpClientAgentData[]).map((agent) =>
                    toLocalAgent(agent, { id: scope.id, label: scope.label })
                  ),
                  connectors: [] as AIConnector[],
                };
              default:
                return {
                  agents: [] as AIAgent[],
                  connectors: [] as AIConnector[],
                };
            }
          })
        );
        if (cancellation.isCancelled) return;
        setAgents(
          uniqBy(
            perScope.flatMap((s) => s.agents),
            (a) => {
              return `${a.platform}:${getAgentId(a)}`;
            }
          )
        );
        setConnectors(perScope.flatMap((s) => s.connectors));
        setLoading(false);
      } catch (err) {
        if (cancellation.isCancelled) return;
        setError(err as Error);
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [args.assessmentId, args.jobId, scopesKey, authFetch],
    useCallback((err: unknown) => {
      setError(err as Error);
      setLoading(false);
    }, [])
  );

  return { agents, connectors, loading, error };
};
